import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { authAPI } from "../services/api";
import logger from "../utils/logger";
import { trackEvent } from "../utils/analytics";
import {
  auth,
  googleProvider,
  signInWithPopup,
} from "../config/firebase";

const completeGoogleSignIn = async (firebaseUser) => {
  const idToken = await firebaseUser.getIdToken(/* forceRefresh */ true);
  const response = await authAPI.googleAuth(idToken);
  const { token } = response.data;
  localStorage.setItem("token", token);
  api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  trackEvent("login", { method: "google" });
  return { token, user: response.data.user };
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  // Handle logout from API interceptor
  useEffect(() => {
    const handleAuthLogout = () => {
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem("token");
      delete api.defaults.headers.common["Authorization"];
      const currentPath = window.location.pathname;
      const isProtectedRoute = currentPath.startsWith("/profile");

      if (isProtectedRoute && currentPath !== "/login") {
        navigate("/login");
      }
    };

    const handleTokenCleared = () => {
      setUser(null);
      setIsAuthenticated(false);
    };

    window.addEventListener("auth:logout", handleAuthLogout);
    window.addEventListener("auth:token_cleared", handleTokenCleared);
    return () => {
      window.removeEventListener("auth:logout", handleAuthLogout);
      window.removeEventListener("auth:token_cleared", handleTokenCleared);
    };
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;

    const token = localStorage.getItem("token");
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      authAPI
        .getProfile()
        .then((response) => {
          if (cancelled) return;
          setUser(response.data.user);
          setIsAuthenticated(true);
        })
        .catch((error) => {
          logger.error("Profile fetch error:", error);
          localStorage.removeItem("token");
          delete api.defaults.headers.common["Authorization"];
          setIsAuthenticated(false);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    } else {
      setLoading(false);
      setIsAuthenticated(false);
    }

    return () => {
      cancelled = true;
    };
  }, []);

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      logger.log("Registration response:", response.data);
      const { token } = response.data;
      localStorage.setItem("token", token);
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setUser(response.data.user);
      setIsAuthenticated(true);
      trackEvent("sign_up", { method: "email" });
      return { success: true };
    } catch (error) {
      logger.error("Registration error:", error);
      setIsAuthenticated(false);
      return {
        success: false,
        message: error.response?.data?.message || "Registration failed",
      };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      logger.log("Login response:", response.data);
      const { token } = response.data;
      localStorage.setItem("token", token);
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setUser(response.data.user);
      setIsAuthenticated(true);
      trackEvent("login", { method: "email" });
      return { success: true };
    } catch (error) {
      logger.error("Login error:", error);
      setIsAuthenticated(false);
      return {
        success: false,
        message: error.response?.data?.message || "Login failed",
      };
    }
  };

  const googleAuth = async ({ redirectTo = "/home", cartData } = {}) => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const { user: appUser } = await completeGoogleSignIn(result.user);
      setUser(appUser);
      setIsAuthenticated(true);
      navigate(redirectTo, { state: cartData, replace: true });
      return { success: true };
    } catch (error) {
      if (
        error.code === "auth/popup-closed-by-user" ||
        error.code === "auth/cancelled-popup-request"
      ) {
        return { success: false, cancelled: true };
      }
      if (error.code === "auth/popup-blocked") {
        return {
          success: false,
          message:
            "Pop-up was blocked by your browser. Please allow pop-ups for this site and try again.",
        };
      }
      logger.error("Google auth error:", error);
      setIsAuthenticated(false);
      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.message ||
          "Google authentication failed. Please try again.",
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete api.defaults.headers.common["Authorization"];
    const {
      clearUserPreferences,
      clearSavedDealsCache,
      clearFollowedBrandsCache,
    } = require("../utils/userPreferences");
    clearUserPreferences();
    clearSavedDealsCache();
    clearFollowedBrandsCache();
    setUser(null);
    setIsAuthenticated(false);
    navigate("/login");
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    register,
    login,
    googleAuth,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
